import React from "react";
import { useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Flex, Timeline, Typography, Tag, Button, Dropdown, Popover, theme } from "antd";
import { GlassCard } from "../../../../shared/components";
import type { MenuProps } from "antd";
import {
    ClockCircleOutlined,
    SyncOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    RobotOutlined,
    EyeOutlined,
    MoreOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { IAutomationTask, AutomationTaskStep, AutomationTaskStatus } from "../../../../models/automation-task";

interface OrderAutomationTasksProps {
    automationTasks?: IAutomationTask[];
    onOpenFilesModal?: (analysisResult: any) => void;
    onOpenAssemblyFilesModal?: (task: IAutomationTask) => void;
    onRerunTask?: (task: IAutomationTask) => void;
    rerunningTaskId?: number | null;
}

export const OrderAutomationTasks: React.FC<OrderAutomationTasksProps> = ({
    automationTasks,
    onOpenFilesModal,
    onOpenAssemblyFilesModal,
    onRerunTask,
    rerunningTaskId = null,
}) => {
    const _t = useTranslate();
    const { token } = theme.useToken();

    const getStatusIcon = (status: AutomationTaskStatus | string) => {
        const style = { fontSize: "1.5em" };
        switch (status) {
            case AutomationTaskStatus.pending:
                return <ClockCircleOutlined style={{ ...style, color: token.colorWarning }} />;
            case AutomationTaskStatus.in_progress:
                return <SyncOutlined spin style={{ ...style, color: token.colorPrimary }} />;
            case AutomationTaskStatus.completed:
                return <CheckCircleOutlined style={{ ...style, color: token.colorSuccess }} />;
            case AutomationTaskStatus.failed:
                return <CloseCircleOutlined style={{ ...style, color: token.colorError }} />;
            default:
                return <ClockCircleOutlined style={style} />;
        }
    };

    const getStatusColor = (status: AutomationTaskStatus | string) => {
        switch (status) {
            case AutomationTaskStatus.pending:
                return "orange";
            case AutomationTaskStatus.in_progress:
                return "blue";
            case AutomationTaskStatus.completed:
                return "green";
            case AutomationTaskStatus.failed:
                return "red";
            default:
                return "default";
        }
    };

    const getStepLabel = (step: AutomationTaskStep | string) => {
        switch (step) {
            case AutomationTaskStep.decrypt:
                return _t("orders.automationTasks.steps.decrypt");
            case AutomationTaskStep.analyze:
                return _t("orders.automationTasks.steps.analyze");
            case AutomationTaskStep.tuner_search:
                return _t("orders.automationTasks.steps.tuner_search");
            case AutomationTaskStep.assembly:
                return _t("orders.automationTasks.steps.assembly");
            default:
                return step;
        }
    };

    const getStatusLabel = (status: AutomationTaskStatus | string) => {
        switch (status) {
            case AutomationTaskStatus.pending:
                return _t("orders.automationTasks.status.pending");
            case AutomationTaskStatus.in_progress:
                return _t("orders.automationTasks.status.in_progress");
            case AutomationTaskStatus.completed:
                return _t("orders.automationTasks.status.completed");
            case AutomationTaskStatus.failed:
                return _t("orders.automationTasks.status.failed");
            default:
                return status;
        }
    };

    const formatError = (error: unknown): string => {
        if (!error) return "";
        if (typeof error === "string") return error;
        if (typeof error === "object" && error !== null) {
            const errorObject = error as { message?: string; stack?: string };
            if (errorObject.stack) return errorObject.stack;
            if (errorObject.message) return errorObject.message;
        }
        return JSON.stringify(error);
    };

    const renderStatusTag = (task: IAutomationTask) => {
        const statusTag = (
            <Tag color={getStatusColor(task.status)} className={task.error ? "cursor-pointer" : undefined}>
                {getStatusLabel(task.status)}
            </Tag>
        );
        if (!task.error) return statusTag;
        return (
            <Popover
                title={_t("orders.automationTasks.error")}
                content={
                    <Typography.Paragraph
                        className="!mb-0 max-w-md max-h-64 overflow-auto whitespace-pre-wrap text-xs"
                        copyable
                    >
                        {formatError(task.error)}
                    </Typography.Paragraph>
                }
                trigger="click"
            >
                {statusTag}
            </Popover>
        );
    };

    const sortedTasks = automationTasks
        ? [...automationTasks].sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        : [];

    return (
        <GlassCard className="shadow-lg mt-6 !px-6">
            <Flex align="center" gap="small">
                <RobotOutlined style={{ color: token.colorPrimary }} />
                <Typography.Title level={5} style={{ margin: 0, fontWeight: "bold" }}>
                    {_t("orders.automationTasks.title")}
                </Typography.Title>
            </Flex>
            <div className="px-2">
                {sortedTasks.length > 0 ? (
                    <Timeline
                        mode="left"
                        className="mt-12"
                        items={sortedTasks.map((task) => ({
                            key: task.id,
                            dot: getStatusIcon(task.status),
                            children: (
                                <div className="ml-4 flex w-full justify-between items-start pb-0">
                                    <div className="flex flex-col">
                                        <div className="flex items-center gap-2 mb-1">
                                            <Typography.Text strong>{getStepLabel(task.step)}</Typography.Text>
                                            {renderStatusTag(task)}
                                        </div>
                                        {task.step === AutomationTaskStep.analyze &&
                                            task.status === AutomationTaskStatus.completed &&
                                            task.metadata?.analysisResult?.projects &&
                                            Array.isArray(task.metadata.analysisResult.projects) &&
                                            task.metadata.analysisResult.projects.length > 0 &&
                                            onOpenFilesModal && (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => onOpenFilesModal(task.metadata.analysisResult)}
                                                    className="mt-2"
                                                >
                                                    {_t("orders.automationTasks.viewFiles")}
                                                </Button>
                                            )}
                                        {task.step === AutomationTaskStep.assembly &&
                                            task.status === AutomationTaskStatus.completed &&
                                            task.metadata?.ecuxFileId &&
                                            task.metadata?.modifiedBinFileId &&
                                            onOpenAssemblyFilesModal && (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => onOpenAssemblyFilesModal(task)}
                                                    className="mt-2"
                                                >
                                                    {_t("orders.automationTasks.viewAssemblyFiles")}
                                                </Button>
                                            )}
                                    </div>
                                    <Flex align="center" gap="small" className="shrink-0 ml-4">
                                        <Typography.Text
                                            type="secondary"
                                            style={{ whiteSpace: "nowrap" }}
                                            className="text-right"
                                        >
                                            <DateField className="text-xs" value={task.createdAt} format="DD MMM YYYY" />
                                            <br />
                                            <DateField className="text-xs" value={task.createdAt} format="HH:mm" />
                                        </Typography.Text>
                                        {onRerunTask && (
                                            <Dropdown
                                                trigger={["click"]}
                                                menu={{
                                                    items: [
                                                        {
                                                            key: "rerun",
                                                            label: _t("orders.automationTasks.rerun"),
                                                            icon: <ReloadOutlined />,
                                                            disabled:
                                                                task.status === AutomationTaskStatus.pending ||
                                                                task.status === AutomationTaskStatus.in_progress ||
                                                                rerunningTaskId === task.id,
                                                            onClick: () => onRerunTask(task),
                                                        },
                                                    ] as MenuProps["items"],
                                                }}
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<MoreOutlined />}
                                                    loading={rerunningTaskId === task.id}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </Dropdown>
                                        )}
                                    </Flex>
                                </div>
                            ),
                        }))}
                    />
                ) : (
                    <div className="mt-8 text-center py-8">
                        <Typography.Text type="secondary">{_t("orders.automationTasks.noTasks")}</Typography.Text>
                    </div>
                )}
            </div>
        </GlassCard>
    );
};
